import { Button, Flex, NumberInput, Paper, Text } from '@mantine/core'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'
import { getUsdRate, updateUsdRate } from 'src/apis/settingAPI'
import { useModal } from 'src/context/ModalContext'
import { formatDateTime } from 'src/variable/constant'

// UD-564: same limits as the server (greater than 1000 and less than 1000000),
// a guard against typing 26 or 26,000,000.
export const USD_RATE_MIN = 1000
export const USD_RATE_MAX = 1000000

export const validateUsdRate = (value: number | '' | undefined) => {
	if (value === '' || value === undefined || Number.isNaN(Number(value))) {
		return 'Enter the rate in dong for 1 US dollar, for example 26,000'
	}
	if (Number(value) <= USD_RATE_MIN || Number(value) >= USD_RATE_MAX) {
		return `The rate must be greater than ${USD_RATE_MIN.toLocaleString(
			'en-US',
		)} and less than ${USD_RATE_MAX.toLocaleString('en-US')}`
	}
	return ''
}

interface UsdRateCardProps {
	inputRef?: React.Ref<HTMLInputElement>
}

function UsdRateCard({ inputRef }: UsdRateCardProps) {
	const { openModal, openError } = useModal()
	const [value, setValue] = useState<number | ''>('')
	const [updatedAt, setUpdatedAt] = useState<string | null>(null)
	const [error, setError] = useState('')
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)

	const handleGet = useCallback(async () => {
		setLoading(true)
		try {
			const res = await getUsdRate()
			const object = res?.data?.results?.object
			setValue(
				typeof object?.usd_vnd_rate === 'number' ? object.usd_vnd_rate : '',
			)
			setUpdatedAt(object?.updated_at || null)
		} catch (e) {
			openError(e)
		} finally {
			setLoading(false)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		handleGet()
	}, [handleGet])

	const handleSave = async () => {
		const message = validateUsdRate(value)
		setError(message)
		if (message) return
		setSaving(true)
		try {
			const res = await updateUsdRate(Number(value))
			const object = res?.data?.results?.object
			if (typeof object?.usd_vnd_rate === 'number') {
				setValue(object.usd_vnd_rate)
			}
			setUpdatedAt(object?.updated_at || null)
			openModal({ type: 'success', label: 'USD rate saved' })
		} catch (e) {
			openError(e)
		} finally {
			setSaving(false)
		}
	}

	return (
		<Paper withBorder p="md" radius="md" sx={{ width: '100%' }}>
			<Flex gap={16} align="flex-start" wrap="wrap">
				<NumberInput
					ref={inputRef}
					sx={{ width: 320 }}
					label="USD rate (1 $ = ? đ)"
					description="Used to show Course prices in $ in the app. Payments are still charged in VND."
					value={value}
					onChange={(next) => {
						setValue(next)
						setError('')
					}}
					min={0}
					precision={2}
					removeTrailingZeros
					hideControls
					disabled={loading}
					error={error}
					formatter={(input) =>
						Number.isNaN(parseFloat(input))
							? ''
							: `${input}`.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',')
					}
					parser={(input) => input.replace(/,/g, '')}
				/>
				<Button
					mt={52}
					variant="filled"
					loading={saving}
					disabled={loading}
					onClick={handleSave}
				>
					Save
				</Button>
			</Flex>
			<Text size="sm" c="dimmed" mt={8}>
				Last updated:{' '}
				{updatedAt ? dayjs(updatedAt).format(formatDateTime) : 'not set yet'}
			</Text>
		</Paper>
	)
}

export default React.memo(UsdRateCard)
